import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik, FormikHelpers } from 'formik';
import { handleOnClickEvent, handleKeyEvent } from 'Utils';
import { Button, Loading } from '@brighterion/br-ui';
import appStyles from 'Routes/Routes.css';
import ButtonGroup from 'Components/ButtonGroup';
import PageHeader from 'Components/PageHeader';
import ScriptOverview from 'Components/ScriptOverview';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import EditRuleInfoOverview from '../EditRuleInfoOverview';

import styles from './EditAdvancedRuleForm.css';

type EditAdvancedRuleFormProps = {
    ruleId: string;
    subjectIdentifier?: string;
    globalRule: Rule;
    parameterData: UseGetParametersResponse | undefined;
    listData: ListApiGetResponse;
    rulesType: RulesType;
    overrideRule?: Rule;
    pageHeader: string;
    mutation: any;
    selectedRowId: string;
    formRef?: any;
    actionType: OverrideAction;
};

const EditAdvancedRuleForm = ({
    ruleId,
    globalRule,
    parameterData,
    listData,
    rulesType,
    subjectIdentifier,
    overrideRule,
    pageHeader,
    mutation,
    selectedRowId,
    formRef,
    actionType
}: EditAdvancedRuleFormProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [openModal, setOpenModal] = useState(false);
    const currentRule =
        rulesType === 'merchantOverrides' || rulesType === 'tenantOverrides' || rulesType === 'tenantGroupOverrides'
            ? overrideRule
            : globalRule;

    const handleCancel = () => {
        switch (rulesType) {
            case 'globalRules':
                history.push(`/global-rules?selectedRowId=${selectedRowId}`);
                break;

            case 'merchantOverrides':
                history.push(`/overrides/merchant/${subjectIdentifier}?selectedRowId=${selectedRowId}`);
                break;

            case 'tenantGroupOverrides':
                history.push(`/tenants/tenant-group/${subjectIdentifier}?selectedRowId=${selectedRowId}`);
                break;

            default:
                history.push('/global-rules');
                break;
        }
    };

    const formikInitialValues = {
        ...currentRule,
        active: currentRule?.parameters.active,
        lists: currentRule?.lists,
        expression: {
            expText: currentRule?.expression?.expText ?? '',
            inputs: currentRule?.expression?.inputs,
            functionCalls: currentRule?.expression?.functionCalls
        },
        comment: '',
        /** @note Bright Script Editor is read-only and therefore the script is always valid. */
        isScriptValidated: true
    };

    const handleTrackEventClick = () => {
        let clickIdentifier = '';
        let text = '';
        if (actionType === 'add') {
            if (rulesType === 'tenantGroupOverrides') {
                clickIdentifier = 'Advanced Tenant Group Rule - Add Override';
                text = 'Add Advanced Tenant Group Rule Override';
            } else if (rulesType === 'tenantOverrides') {
                clickIdentifier = 'Global Rules - Add Tenant Override';
                text = 'Add Tenant Override';
            }
        } else if (actionType === 'edit') {
            if (rulesType === 'tenantGroupOverrides') {
                clickIdentifier = 'Advanced Tenant Group Rule - Edit Override';
                text = 'Edit Advanced Tenant Group Rule Override';
            }
        }

        if (!clickIdentifier || !text) {
            return;
        }

        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier,
                text
            }
        });
    };

    /** TemplatedRuleFormValues don't have types. Most values are type any. */
    const handleSave = (formValues: TemplatedRuleFormValues, actions: FormikHelpers<typeof formikInitialValues>) => {
        const { lists, comment, active } = formValues;
        /** @note Actions is required by the endpoint, but not used  */
        mutation.mutate({
            rules: [
                {
                    id: ruleId,
                    actions: formValues.actions,
                    parameters: {
                        active
                    },
                    inputs: currentRule?.inputs,
                    lists,
                    nonTemplated: currentRule?.nonTemplated
                }
            ],
            comments: {
                [ruleId]: comment
            }
        });
        handleTrackEventClick();

        actions.setSubmitting(false);
    };

    return (
        <Formik
            enableReinitialize
            initialValues={formikInitialValues}
            onSubmit={(formValues, actions) => handleSave(formValues, actions)}
            innerRef={formRef}
        >
            {(formikProps) => {
                const { dirty, isValid, isSubmitting, handleSubmit } = formikProps;
                const disableSave = !dirty || !isValid;

                const submitForm = () => {
                    handleSubmit();
                };

                return (
                    <div className={appStyles.landingPage}>
                        <PageHeader>
                            <PageHeader.Left className={styles.titleText}>
                                {pageHeader}
                                {!currentRule?.nonTemplated && (
                                    <span className={styles.ruleTypeText}>{t('DEFAULT RULE')}</span>
                                )}
                            </PageHeader.Left>
                            <PageHeader.Right className={styles.rightButtonContainer}>
                                {mutation.isLoading ? (
                                    <Loading size='sm' fillName='orange' />
                                ) : (
                                    <ButtonGroup direction='row'>
                                        <Button
                                            disabled={isSubmitting}
                                            dataTestId='editForm-cancel-btn'
                                            variant='tertiary'
                                            size='medium'
                                            onKeyDown={handleKeyEvent(handleCancel)}
                                            onClick={handleOnClickEvent(handleCancel)}
                                        >
                                            {t('Cancel')}
                                        </Button>

                                        <Button
                                            dataTestId='editForm-save-btn'
                                            variant='primary'
                                            size='small'
                                            onClick={handleOnClickEvent(submitForm)}
                                            onKeyDown={handleKeyEvent(submitForm)}
                                            disabled={disableSave || isSubmitting}
                                        >
                                            {isSubmitting ? t('Saving...') : t('Save Rule')}
                                        </Button>
                                    </ButtonGroup>
                                )}
                            </PageHeader.Right>
                        </PageHeader>
                        <EditRuleInfoOverview
                            currentGlobalRule={globalRule!}
                            currentOverrideRule={overrideRule!}
                            listData={listData}
                            rulesType={rulesType}
                            isSubmitting={isSubmitting}
                            openModal={openModal}
                            setOpenModal={setOpenModal}
                            subjectIdentifier={subjectIdentifier}
                        />
                        {/** @note Rule Script Overview get all props from useFormikContext()  */}
                        <ScriptOverview
                            scriptType='rule'
                            inputFields={[{ name: 'comment', label: 'Comment (Optional)', className: styles.comment }]}
                            leftTitle={t('Rule Statement')}
                            parameterData={parameterData}
                            listData={listData?.result}
                            readOnly
                        />
                    </div>
                );
            }}
        </Formik>
    );
};

export default EditAdvancedRuleForm;
